/**
 * Header Fitment Selector
 * Handles Make/Model dropdown interactions and form submission
 */

class HeaderFitmentSelector extends HTMLElement {
  constructor() {
    super();
    this.fitmentData = null;
    this.makeSelect = null;
    this.modelSelect = null;
    this.submitButton = null;
    this.form = null;
    this.showProductCount = false;
    this.STORAGE_KEY = 'lps_fitment_selection';
  }

  connectedCallback() {
    this.form = this.querySelector('[data-fitment-selector-form]');
    if (!this.form) return;

    // Get settings from data attributes
    this.showProductCount = this.form.dataset.showProductCount === 'true';

    // Get fitment data from JSON script tag
    const dataScript = this.querySelector('[data-fitment-data]');
    if (dataScript) {
      try {
        this.fitmentData = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('Error parsing fitment data:', e);
        return;
      }
    }

    this.makeSelect = this.form.querySelector('[data-fitment-make-select]');
    this.modelSelect = this.form.querySelector('[data-fitment-model-select]');
    this.submitButton = this.form.querySelector('[data-fitment-submit-button]');

    if (!this.makeSelect || !this.modelSelect || !this.submitButton) return;

    // Bind event handlers to preserve 'this' context
    this.boundHandleMakeChange = () => this.handleMakeChange();
    this.boundHandleModelChange = () => this.handleModelChange();
    this.boundHandleSubmit = (e) => this.handleSubmit(e);

    // Initialize event listeners
    this.makeSelect.addEventListener('change', this.boundHandleMakeChange);
    this.modelSelect.addEventListener('change', this.boundHandleModelChange);
    this.form.addEventListener('submit', this.boundHandleSubmit);

    // Disabled: Auto-loading saved selections
    // This was causing the dropdowns to reset to previous selections on page load
    // User should start fresh each time
    // requestAnimationFrame(() => {
    //   this.loadSavedSelections();
    // });
  }

  /**
   * Save fitment selections to localStorage
   */
  saveSelections(makeValue, makeName, modelValue) {
    try {
      const selections = {
        makeValue,
        makeName,
        modelValue,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(selections));
    } catch (e) {
      console.error('Error saving fitment selections:', e);
    }
  }

  /**
   * Load saved selections from localStorage and restore dropdowns
   */
  loadSavedSelections() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (!saved) return;

      const selections = JSON.parse(saved);
      if (!selections.makeValue || !selections.modelValue) return;

      // Remove event listeners temporarily to prevent any unwanted triggers
      this.makeSelect.removeEventListener('change', this.boundHandleMakeChange);
      this.modelSelect.removeEventListener('change', this.boundHandleModelChange);

      // Set the make dropdown
      this.makeSelect.value = selections.makeValue;

      // Manually populate models without triggering events
      this.populateModels(selections.makeValue);

      // Wait for DOM to update, then set model value
      requestAnimationFrame(() => {
        setTimeout(() => {
          // Verify the model option exists before setting
          const modelOption = Array.from(this.modelSelect.options).find(
            opt => opt.value === selections.modelValue
          );

          if (modelOption) {
            this.modelSelect.value = selections.modelValue;
            this.submitButton.disabled = false;
          }

          // Re-attach event listeners
          this.makeSelect.addEventListener('change', this.boundHandleMakeChange);
          this.modelSelect.addEventListener('change', this.boundHandleModelChange);
        }, 50); // Increased timeout for more reliable rendering
      });
    } catch (e) {
      console.error('Error loading saved fitment selections:', e);
      // Re-attach event listeners even if there's an error
      this.makeSelect.addEventListener('change', this.boundHandleMakeChange);
      this.modelSelect.addEventListener('change', this.boundHandleModelChange);
    }
  }

  /**
   * Populate model dropdown for a given make (without triggering events)
   */
  populateModels(makeValue) {
    // Find the make option
    const makeOption = Array.from(this.makeSelect.options).find(
      opt => opt.value === makeValue
    );

    if (!makeOption) return;

    const makeName = makeOption.dataset.makeName;
    if (!makeName || !this.fitmentData?.makes) return;

    // Find the selected make in the data
    const selectedMake = this.fitmentData.makes.find(
      (make) => make.name === makeName
    );

    if (!selectedMake || !selectedMake.models) return;

    // Clear and repopulate model select
    this.modelSelect.innerHTML = '<option value="">Model</option>';

    // Sort models: numeric-first (0-9) then letters alphabetically
    const sortedModels = [...selectedMake.models].sort((a, b) => {
      const normalize = (value) => value?.toString()?.trim?.() ?? '';
      const aValue = normalize(a.name);
      const bValue = normalize(b.name);
      const aStartsDigit = /^\d/.test(aValue);
      const bStartsDigit = /^\d/.test(bValue);

      if (aStartsDigit && !bStartsDigit) return -1;
      if (!aStartsDigit && bStartsDigit) return 1;

      return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
    });

    sortedModels.forEach((model) => {
      const option = document.createElement('option');
      option.value = model.name;

      // Conditionally show product count based on theme setting
      if (this.showProductCount) {
        const count =
          model.c !== undefined
            ? model.c
            : model.ct !== undefined
            ? model.ct
            : undefined;
        option.textContent = count !== undefined ? `${model.name} (${count})` : model.name;
      } else {
        option.textContent = model.name;
      }

      this.modelSelect.appendChild(option);
    });

    // Enable model select
    this.modelSelect.disabled = false;
  }

  handleMakeChange() {
    const selectedMakeValue = this.makeSelect.value;

    // Clear and disable model select
    this.modelSelect.innerHTML = '<option value="">Model</option>';
    this.modelSelect.disabled = true;
    this.submitButton.disabled = true;

    if (!selectedMakeValue) {
      return;
    }

    // Populate models using the shared method
    this.populateModels(selectedMakeValue);
  }

  handleModelChange() {
    const modelValue = this.modelSelect.value;
    const hasModel = modelValue !== '';
    this.submitButton.disabled = !hasModel;

    // If a model is selected, automatically navigate and save
    if (hasModel) {
      const makeValue = this.makeSelect.value;
      const selectedOption = this.makeSelect.options[this.makeSelect.selectedIndex];
      const makeName = selectedOption?.dataset.makeName;

      if (makeValue && makeName) {
        // Save selections to localStorage
        this.saveSelections(makeValue, makeName, modelValue);

        // Build the collection URL and navigate
        //old url with default shopify:
        // const collectionUrl = `/collections/${makeValue}?sort_by=best-selling&filter.p.m.lps_fitment.fit_model_numbers=${encodeURIComponent(modelValue)}`;
        //new url with new app filter:
        const collectionUrl = `/collections/shop-all?filter.578328=${encodeURIComponent(modelValue)}&filter.578357=${makeValue}`;
        window.location.href = collectionUrl;
      }
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    const makeValue = this.makeSelect.value;
    const modelValue = this.modelSelect.value;

    if (!makeValue || !modelValue) {
      return;
    }

    // Get the make name for the collection URL
    const selectedOption = this.makeSelect.options[this.makeSelect.selectedIndex];
    const makeSlug = selectedOption?.value || makeValue;
    const makeName = selectedOption?.dataset.makeName;

    // Save selections to localStorage
    if (makeName) {
      this.saveSelections(makeValue, makeName, modelValue);
    }

    // Build the collection URL
    // Format: /collections/{make-slug}?sort_by=best-selling&filter.p.m.lps_fitment.fit_model_numbers={model-name}
    const collectionUrl = `/collections/${makeSlug}?sort_by=best-selling&filter.p.m.lps_fitment.fit_model_numbers=${encodeURIComponent(modelValue)}`;

    // Redirect to the filtered collection
    window.location.href = collectionUrl;
  }
}

customElements.define('header-fitment-selector', HeaderFitmentSelector);

// Initialize if form exists without custom element wrapper
document.addEventListener('DOMContentLoaded', () => {
  const forms = document.querySelectorAll('[data-fitment-selector-form]');
  forms.forEach((form) => {
    if (!form.closest('header-fitment-selector')) {
      const wrapper = document.createElement('header-fitment-selector');
      form.parentNode.insertBefore(wrapper, form);
      wrapper.appendChild(form);
    }
  });
});

